/**
 * Pass Manager - Manages multi-pass shader rendering state
 * Handles Buffer A-D passes for feedback effects and pass-to-pass texture passing
 */

// Pass ID constants to avoid magic strings
export const PASS_IDS = {
  MAIN: 'main',
  BUFFER_A: 'bufferA',
  BUFFER_B: 'bufferB',
  BUFFER_C: 'bufferC',
  BUFFER_D: 'bufferD',
};

const BUFFER_ORDER = [PASS_IDS.BUFFER_A, PASS_IDS.BUFFER_B, PASS_IDS.BUFFER_C, PASS_IDS.BUFFER_D];

export class PassManager {
  constructor() {
    // Map of passId -> { code, wgslCode, hasError }
    this.passes = new Map();
    this.activePass = PASS_IDS.MAIN;
    // List of enabled buffer IDs (in execution order)
    this.enabledBuffers = [];

    // Initialize Main only (buffers added on demand)
    this.passes.set(PASS_IDS.MAIN, { code: '', wgslCode: '', hasError: false });
  }

  /**
   * Get pass state by ID
   * @param {string} id - Pass ID ('main', 'bufferA', etc.)
   * @returns {Object|undefined}
   */
  getPass(id) {
    return this.passes.get(id);
  }

  /**
   * Set code for a pass
   * @param {string} id - Pass ID
   * @param {string} code - Slang source code
   */
  setCode(id, code) {
    const pass = this.passes.get(id);
    if (pass) {
      pass.code = code;
    }
  }

  /**
   * Get code for a pass
   * @param {string} id - Pass ID
   * @returns {string}
   */
  getCode(id) {
    return this.passes.get(id)?.code ?? '';
  }

  /**
   * Set compiled WGSL code for a pass
   * @param {string} id - Pass ID
   * @param {string} wgslCode - Compiled WGSL code
   */
  setWgslCode(id, wgslCode) {
    const pass = this.passes.get(id);
    if (pass) {
      pass.wgslCode = wgslCode;
    }
  }

  /**
   * Get compiled WGSL code for a pass
   * @param {string} id - Pass ID
   * @returns {string}
   */
  getWgslCode(id) {
    return this.passes.get(id)?.wgslCode ?? '';
  }

  /**
   * Set error state for a pass
   * @param {string} id - Pass ID
   * @param {boolean} hasError
   */
  setError(id, hasError) {
    const pass = this.passes.get(id);
    if (pass) {
      pass.hasError = hasError;
    }
  }

  /**
   * Check if pass has error
   * @param {string} id - Pass ID
   * @returns {boolean}
   */
  hasError(id) {
    return this.passes.get(id)?.hasError ?? false;
  }

  /**
   * Set the active (currently editing) pass
   * @param {string} id - Pass ID
   */
  setActivePass(id) {
    if (this.passes.has(id)) {
      this.activePass = id;
    }
  }

  /**
   * Get the active pass ID
   * @returns {string}
   */
  getActivePass() {
    return this.activePass;
  }

  /**
   * Add the next available buffer (B -> C -> D)
   * @returns {string|null} The ID of the added buffer, or null if all added
   */
  addNextBuffer() {
    const next = BUFFER_ORDER.find((id) => !this.enabledBuffers.includes(id));
    if (next) {
      this.enabledBuffers.push(next);
      this.passes.set(next, { code: '', wgslCode: '', hasError: false });
      return next;
    }
    return null;
  }

  /**
   * Check if more buffers can be added
   * @returns {boolean}
   */
  canAddBuffer() {
    return this.enabledBuffers.length < BUFFER_ORDER.length;
  }

  /**
   * Remove a buffer by ID
   * @param {string} id - Buffer ID to remove
   * @returns {boolean} True if buffer was removed
   */
  removeBuffer(id) {
    const index = this.enabledBuffers.indexOf(id);
    if (index === -1) return false;

    // Remove from enabled list
    this.enabledBuffers.splice(index, 1);

    // Remove pass data
    this.passes.delete(id);

    // If active pass was removed, switch to Main
    if (this.activePass === id) {
      this.activePass = PASS_IDS.MAIN;
    }

    return true;
  }

  /**
   * Get list of all enabled pass IDs (buffers + main, in execution order)
   * Execution order: bufferA -> bufferB -> bufferC -> bufferD -> main
   * @returns {string[]}
   */
  getEnabledPasses() {
    return [...this.enabledBuffers, PASS_IDS.MAIN];
  }

  /**
   * Get list of enabled buffer IDs only
   * @returns {string[]}
   */
  getEnabledBuffers() {
    return [...this.enabledBuffers];
  }

  /**
   * Check if a buffer is enabled
   * @param {string} id - Buffer ID
   * @returns {boolean}
   */
  isBufferEnabled(id) {
    return this.enabledBuffers.includes(id);
  }

  /**
   * Get buffer display letter (A, B, C, D)
   * @param {string} id - Buffer ID
   * @returns {string}
   */
  static getBufferLetter(id) {
    const index = BUFFER_ORDER.indexOf(id);
    return index >= 0 ? String.fromCharCode(65 + index) : '?';
  }

  /**
   * Get buffer ID from letter
   * @param {string} letter - Buffer letter (A, B, C, D)
   * @returns {string|null}
   */
  static getBufferIdFromLetter(letter) {
    const index = letter.charCodeAt(0) - 65;
    return BUFFER_ORDER[index] ?? null;
  }

  /**
   * Reset to initial state (Main only)
   */
  reset() {
    this.passes.clear();
    this.activePass = PASS_IDS.MAIN;
    this.enabledBuffers = [];
    this.passes.set(PASS_IDS.MAIN, { code: '', wgslCode: '', hasError: false });
  }

  /**
   * Serialize state for URL encoding
   * @returns {Object}
   */
  serialize() {
    const passes = {};
    for (const [id, pass] of this.passes) {
      if (pass.code) {
        passes[id] = pass.code;
      }
    }
    return {
      v: 1,
      a: this.activePass,
      b: this.enabledBuffers,
      p: passes,
    };
  }

  /**
   * Deserialize state from URL data
   * @param {Object} data - Serialized state
   */
  deserialize(data) {
    // Reset state
    this.passes.clear();
    this.enabledBuffers = [];

    // Restore enabled buffers
    const buffers = data.b || [];
    for (const bufferId of buffers) {
      if (BUFFER_ORDER.includes(bufferId)) {
        this.enabledBuffers.push(bufferId);
        this.passes.set(bufferId, { code: '', wgslCode: '', hasError: false });
      }
    }

    // Restore main pass
    this.passes.set(PASS_IDS.MAIN, { code: '', wgslCode: '', hasError: false });

    // Restore pass codes
    if (data.p) {
      for (const [id, code] of Object.entries(data.p)) {
        if (this.passes.has(id)) {
          this.passes.get(id).code = code;
        }
      }
    }

    // Restore active pass
    this.activePass = data.a && this.passes.has(data.a) ? data.a : PASS_IDS.MAIN;
  }
}

// Buffer order constant for external use
export { BUFFER_ORDER };
